import { type ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function Modal({ isOpen, onClose, title, children, footer }: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Round 45 follow-up, item 11 — REAL FIX: "make the VIP Products form
  // vertically scrollable, with a usable Save/Cancel and nothing hidden
  // off-screen." Previously this modal had no height limit at all — a
  // form with many fields (like VIP Products' tier/coins/price/duration/
  // benefits list) just grew past the viewport, pushing the Save/Cancel
  // footer off-screen with no way to scroll down to it. Fixed once here
  // since every admin page's edit dialog shares this one Modal
  // component: the dialog itself is now capped to 90% of the viewport
  // height and laid out as a column (header / scrollable body / footer)
  // so the header and footer always stay visible and only the body
  // between them scrolls — never the whole-page scroll a fixed-position
  // overlay can't actually use.
  return createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-surface-border bg-surface-card shadow-popover">
        <div className="flex shrink-0 items-center justify-between border-b border-surface-border px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button onClick={onClose} className="focus-ring rounded-lg p-1 text-ink-faint hover:bg-surface-raised hover:text-ink" aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex shrink-0 justify-end gap-2 border-t border-surface-border px-5 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmLabel?: string;
  isDangerous?: boolean;
  isLoading?: boolean;
}

/**
 * Every irreversible or money-affecting action in this app (ban, reject a
 * withdrawal, delete a post, revoke an admin, grant VIP) routes through
 * this exact component before the real API call ever fires — a single,
 * consistent "are you sure" step rather than each page inventing its own
 * (or, worse, some pages skipping it).
 */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirm",
  isDangerous = false,
  isLoading = false,
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant={isDangerous ? "danger" : "primary"} onClick={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">{description}</p>
    </Modal>
  );
}
